import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, getDoc, deleteDoc, doc, query, where, orderBy } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

function parseEmails(text) {
  const matches = String(text || '').match(EMAIL_RE) || [];
  return Array.from(new Set(matches.map(e => e.trim().toLowerCase())));
}

// mode: 'tcf' (all engagement codes) or 'company' (only the caller's codes)
export default function InviteParticipants({ user, mode = 'tcf' }) {
  const [codes, setCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(true);
  const [code, setCode] = useState('');
  const [inviteType, setInviteType] = useState('standard');
  const [emailsText, setEmailsText] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const [invites, setInvites] = useState([]);
  const [loadingTracking, setLoadingTracking] = useState(false);
  const [trackingCode, setTrackingCode] = useState('');
  const [search, setSearch] = useState('');

  const emailList = parseEmails(emailsText);

  useEffect(() => {
    const loadCodes = async () => {
      try {
        if (mode === 'company' && user?.email) {
          const snap = await getDoc(doc(db, 'companyAdmins', user.email));
          const data = snap.exists() ? snap.data() : {};
          const list = Array.isArray(data.companyCodes) ? data.companyCodes : (data.companyCode ? [data.companyCode] : []);
          setCodes(list);
          if (list.length === 1) setCode(list[0]);
        } else {
          const snap = await getDocs(collection(db, 'engagements'));
          const list = snap.docs
            .filter(d => d.data().active !== false)
            .map(d => ({ code: d.id, company: d.data().company || '' }));
          setCodes(list);
        }
      } catch (err) {
        setError('Could not load your program codes. ' + (err.message || ''));
      }
      setLoadingCodes(false);
    };
    loadCodes();
  }, [mode, user]);

  // Normalize codes to a list of { code, company } for the dropdown
  const codeOptions = codes.map(c => (typeof c === 'string' ? { code: c, company: '' } : c));

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const name = file.name.toLowerCase();
    if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      import('xlsx').then(XLSX => {
        const reader = new FileReader();
        reader.onload = (ev) => {
          try {
            const wb = XLSX.read(ev.target.result, { type: 'array' });
            let text = '';
            wb.SheetNames.forEach(sn => {
              const rows = XLSX.utils.sheet_to_csv(wb.Sheets[sn]);
              text += '\n' + rows;
            });
            const found = parseEmails(text);
            setEmailsText(prev => Array.from(new Set([...parseEmails(prev), ...found])).join('\n'));
          } catch {
            setError('Could not read that spreadsheet. Try a CSV instead.');
          }
        };
        reader.readAsArrayBuffer(file);
      });
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const found = parseEmails(ev.target.result);
        setEmailsText(prev => Array.from(new Set([...parseEmails(prev), ...found])).join('\n'));
      };
      reader.readAsText(file);
    }
    e.target.value = '';
  };

  const handleSend = async () => {
    setError('');
    setResult(null);
    const cleanCode = code.toUpperCase().trim();
    if (!cleanCode) { setError('Pick a program code first.'); return; }
    if (!emailList.length) { setError('Add at least one email address.'); return; }

    setSending(true);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'sendInvites');
      const res = await fn({ companyCode: cleanCode, emails: emailList, inviteType });
      setResult(res.data);
      setEmailsText('');
      await loadTracking(cleanCode);
    } catch (err) {
      setError(err.message || 'Could not send invites. Try again.');
    }
    setSending(false);
  };

  const loadTracking = async (forCode) => {
    const cleanCode = (forCode || code).toUpperCase().trim();
    if (!cleanCode) { setError('Pick a program code to see its invites.'); return; }
    setLoadingTracking(true);
    setTrackingCode(cleanCode);
    try {
      let inviteDocs = [];
      try {
        const q = query(collection(db, 'invites'), where('companyCode', '==', cleanCode), orderBy('invitedAt', 'desc'));
        const snap = await getDocs(q);
        inviteDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch {
        const snap = await getDocs(query(collection(db, 'invites'), where('companyCode', '==', cleanCode)));
        inviteDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      }

      const userSnap = await getDocs(query(collection(db, 'users'), where('companyCode', '==', cleanCode)));
      const joinedEmails = new Set();
      userSnap.docs.forEach(d => {
        const em = String(d.data().email || '').trim().toLowerCase();
        if (em) joinedEmails.add(em);
      });

      setInvites(inviteDocs.map(inv => ({ ...inv, joined: joinedEmails.has(String(inv.email || '').trim().toLowerCase()) })));
    } catch (err) {
      setError('Could not load invite tracking. ' + (err.message || ''));
    }
    setLoadingTracking(false);
  };

  const [resending, setResending] = useState({});

  // Re-send the invite email. The server updates the same tracking row, so
  // this never creates duplicates.
  const handleResend = async (emails) => {
    const cleanCode = trackingCode || code.toUpperCase().trim();
    if (!cleanCode || !emails.length) return;
    setError('');
    const marks = {};
    emails.forEach(e => { marks[e] = true; });
    setResending(prev => ({ ...prev, ...marks }));
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'sendInvites');
      // The server accepts at most 200 addresses per call.
      for (let i = 0; i < emails.length; i += 200) {
        await fn({ companyCode: cleanCode, emails: emails.slice(i, i + 200), inviteType });
      }
      await loadTracking(cleanCode);
    } catch (err) {
      setError(err.message || 'Could not resend. Try again.');
    }
    setResending(prev => {
      const next = { ...prev };
      emails.forEach(e => { delete next[e]; });
      return next;
    });
  };

  const notJoined = invites.filter(i => !i.joined).map(i => i.email);

  const handleResendAllNotJoined = () => {
    if (!notJoined.length) return;
    if (!window.confirm(`Resend the invite email to ${notJoined.length} ${notJoined.length === 1 ? 'person' : 'people'} who haven't joined yet?`)) return;
    handleResend(notJoined);
  };

  const handleDeleteInvite = async (inv) => {
    if (!window.confirm(`Remove the invite for ${inv.email}? (This only removes the tracking record; it does not unsend the email.)`)) return;
    try {
      await deleteDoc(doc(db, 'invites', inv.id));
      setInvites(prev => prev.filter(x => x.id !== inv.id));
    } catch (err) {
      setError('Could not remove that invite. ' + (err.message || ''));
    }
  };

  const joinedCount = invites.filter(i => i.joined).length;
  const shownInvites = search.trim()
    ? invites.filter(i => i.email.toLowerCase().includes(search.trim().toLowerCase()))
    : invites;

  return (
    <div style={s.container}>
      <div style={s.headerBlock}>
        <h1 style={s.heading}>Invite Participants</h1>
        <p style={s.sub}>
          Pick a program code, add the email addresses (paste them or upload a CSV/Excel list), and send.
          Each person gets a branded email with the code and a signup link. Track who has joined below.
        </p>
      </div>

      <div style={s.card}>
        <label style={s.label}>Program code</label>
        {loadingCodes ? (
          <div style={s.hint}>Loading your codes…</div>
        ) : codeOptions.length === 0 ? (
          <div style={{ ...s.msgBox, ...s.msgError }}>
            You don't have any program codes assigned yet. Ask your TCF contact to set one up.
          </div>
        ) : (
          <select style={{ ...s.input, maxWidth: '340px' }} value={code} onChange={e => setCode(e.target.value)}>
            <option value="">Choose a code…</option>
            {codeOptions.map(c => (
              <option key={c.code} value={c.code}>{c.code}{c.company ? ` — ${c.company}` : ''}</option>
            ))}
          </select>
        )}

        <label style={s.label}>Which invitation</label>
        <div style={s.radioRow}>
          <label style={s.radioLabel}>
            <input type="radio" name="inviteType" checked={inviteType === 'standard'} onChange={() => setInviteType('standard')} />
            <span><strong>Standard invitation</strong>: the full get-started walkthrough.</span>
          </label>
          <label style={s.radioLabel}>
            <input type="radio" name="inviteType" checked={inviteType === 'officehours'} onChange={() => setInviteType('officehours')} />
            <span><strong>Office hours invitation</strong>: for programs with AI office hours. Set the day, time, and location on the Email Wording page first.</span>
          </label>
        </div>

        <label style={s.label}>Email addresses</label>
        <textarea
          style={{ ...s.input, minHeight: '120px', resize: 'vertical' }}
          placeholder={"Paste emails here — one per line or separated by commas.\n\njane@company.com\njohn@company.com"}
          value={emailsText}
          onChange={e => setEmailsText(e.target.value)}
        />
        <div style={s.fileRow}>
          <label style={s.uploadBtn}>
            Upload CSV / Excel
            <input type="file" accept=".csv,.xlsx,.xls,text/csv" onChange={handleFile} style={{ display: 'none' }} />
          </label>
          <span style={s.hint}>{emailList.length} address{emailList.length === 1 ? '' : 'es'} ready.</span>
        </div>

        <div style={s.actionRow}>
          <button style={s.sendBtn} onClick={handleSend} disabled={sending || !codeOptions.length}>
            {sending ? 'Sending…' : `Send ${emailList.length || ''} invite${emailList.length === 1 ? '' : 's'}`.trim()}
          </button>
        </div>

        {error && <div style={{ ...s.msgBox, ...s.msgError }}>{error}</div>}

        {result && (
          <div style={{ ...s.msgBox, ...(result.failedCount ? s.msgWarn : s.msgSuccess) }}>
            Sent {result.sentCount} invite{result.sentCount === 1 ? '' : 's'}.
            {result.failedCount > 0 && ` ${result.failedCount} failed.`}
            {result.failedCount > 0 && (
              <ul style={s.failList}>
                {result.results.filter(r => !r.ok).map((r, i) => <li key={i}>{r.email} — {r.error}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      <div style={s.trackHeader}>
        <div style={s.trackTitle}>
          Invite tracking{trackingCode ? ` — ${trackingCode}` : ''}
          {invites.length > 0 && ` (${joinedCount}/${invites.length} joined)`}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {invites.length > 0 && (
            <input style={s.searchInput} placeholder="Search email…" value={search} onChange={e => setSearch(e.target.value)} />
          )}
          {notJoined.length > 0 && (
            <button style={s.resendAllBtn} onClick={handleResendAllNotJoined} disabled={Object.keys(resending).length > 0}>
              {Object.keys(resending).length > 0 ? 'Resending…' : `Resend all not joined (${notJoined.length})`}
            </button>
          )}
          <button style={s.refreshBtn} onClick={() => loadTracking()} disabled={loadingTracking || !code}>
            {loadingTracking ? 'Loading…' : 'Show / refresh'}
          </button>
        </div>
      </div>

      {invites.length === 0 && !loadingTracking && (
        <div style={s.empty}>Pick a code above and click “Show / refresh” to see who has been invited and who has joined.</div>
      )}

      {invites.length > 0 && (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Email</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Email delivered</th>
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {shownInvites.map(inv => (
                <tr key={inv.id}>
                  <td style={s.td}>{inv.email}</td>
                  <td style={s.td}>
                    <span style={{ ...s.pill, ...(inv.joined ? s.pillJoined : s.pillInvited) }}>
                      {inv.joined ? '✓ Joined' : 'Invited'}
                    </span>
                  </td>
                  <td style={s.td}>
                    {inv.emailSent
                      ? <span style={s.sentYes}>Sent</span>
                      : <span style={s.sentNo}>Failed{inv.sendError ? `: ${inv.sendError}` : ''}</span>}
                  </td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                      {!inv.joined && (
                        <button style={s.resendBtn} onClick={() => handleResend([inv.email])} disabled={!!resending[inv.email]}>
                          {resending[inv.email] ? 'Sending…' : 'Resend'}
                        </button>
                      )}
                      <button style={s.deleteBtn} onClick={() => handleDeleteInvite(inv)}>Remove</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const s = {
  container: { padding: '24px 32px', maxWidth: '1000px' },
  headerBlock: { marginBottom: '24px' },
  heading: { fontFamily: 'Playfair Display, serif', fontSize: '26px', fontWeight: '900', color: '#1B2A4A', marginBottom: '8px' },
  sub: { fontSize: '13px', color: '#3D3D3D', fontWeight: '300', lineHeight: '1.6', maxWidth: '720px' },
  card: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '22px 24px', marginBottom: '32px' },
  label: { display: 'block', fontSize: '11px', fontWeight: '600', color: '#1B2A4A', letterSpacing: '0.4px', textTransform: 'uppercase', margin: '14px 0 5px' },
  input: {
    width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 14px',
    fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D',
    background: '#fff', outline: 'none', boxSizing: 'border-box',
  },
  hint: { fontSize: '11px', color: '#8A8A8A' },
  radioRow: { display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' },
  radioLabel: { display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: '#3D3D3D', fontFamily: 'DM Sans, sans-serif', lineHeight: '1.5', cursor: 'pointer' },
  fileRow: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px', flexWrap: 'wrap' },
  uploadBtn: {
    display: 'inline-block', background: '#F2F0EC', color: '#1B2A4A', border: '1.5px solid #E8E6E2',
    padding: '8px 16px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
  },
  actionRow: { marginTop: '18px' },
  sendBtn: {
    background: '#1B2A4A', color: '#fff', border: 'none', padding: '12px 26px',
    fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
  },
  msgBox: { fontSize: '13px', padding: '10px 14px', marginTop: '16px', lineHeight: '1.5' },
  msgError: { background: '#fdecea', color: '#a32d2d' },
  msgSuccess: { background: '#eaf3de', color: '#3b6d11' },
  msgWarn: { background: '#fdf3e2', color: '#854f0b' },
  failList: { margin: '8px 0 0', paddingLeft: '18px', fontSize: '12px' },
  trackHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '16px', flexWrap: 'wrap' },
  trackTitle: { fontFamily: 'Playfair Display, serif', fontSize: '18px', fontWeight: '700', color: '#1B2A4A' },
  searchInput: { border: '1.5px solid #E8E6E2', padding: '8px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#fff', outline: 'none', minWidth: '160px' },
  refreshBtn: {
    background: '#F2F0EC', color: '#1B2A4A', border: '1.5px solid #E8E6E2', padding: '9px 16px',
    fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
  },
  empty: { padding: '20px', background: '#F2F0EC', color: '#3D3D3D', fontSize: '13px', fontFamily: 'DM Sans, sans-serif' },
  tableWrap: { background: '#fff', border: '1.5px solid #E8E6E2', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontFamily: 'DM Sans, sans-serif' },
  th: { textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', color: '#8A8A8A', padding: '12px 16px', borderBottom: '1.5px solid #E8E6E2', whiteSpace: 'nowrap' },
  td: { fontSize: '13px', color: '#3D3D3D', padding: '11px 16px', borderBottom: '1px solid #F2F0EC' },
  pill: { display: 'inline-block', fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '2px' },
  pillJoined: { background: '#eaf3de', color: '#3b6d11' },
  pillInvited: { background: '#F2F0EC', color: '#8A8A8A' },
  sentYes: { color: '#3b6d11', fontSize: '12px' },
  sentNo: { color: '#a32d2d', fontSize: '12px' },
  deleteBtn: { background: '#fff', color: '#a32d2d', border: '1.5px solid #e6c4c4', padding: '6px 12px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  resendBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '6px 12px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap' },
  resendAllBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '9px 16px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap' },
};
